// API base URL configuration
// import axios from 'axios';

export const API_BASE_URL = 'https://api.ibrahim-magdy.com/api';

// Multi-tenant configuration
export const TENANT_ID = 'tenant2';

// Memory storage for auth data
class AuthService {
  private userData: any = null;
  private refreshPromise: Promise<boolean> | null = null;
  private authChangeListeners: (() => void)[] = [];
  private lastRefreshAttempt: number = 0;
  private readonly minRefreshInterval: number = 5000; // 5 seconds minimum between refresh attempts

  constructor() {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        this.userData = parsedAuth.userData;
      } catch (error) {
        console.error('Failed to parse saved auth data');
        this.clearAuthData();
      }
    }
  }

  setAuthData(token: string, userData: any): void {
    if (!token) {
      console.error('Attempted to set null or undefined token');
      return;
    }
    this.userData = userData;
    const authData = {
      token,
      userData,
      timestamp: Date.now()
    };
    localStorage.setItem('authData', JSON.stringify(authData));
    localStorage.setItem('isAuthenticated', 'true');
    this.notifyAuthChange();
  }

  getToken(): string | null {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        return parsedAuth.token || null;
      } catch (error) {
        console.error('Failed to parse saved auth data when getting token');
        this.clearAuthData();
        return null;
      }
    }
    return null;
  }

  getUserData(): any {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        return parsedAuth.userData || null;
      } catch (error) {
        console.error('Failed to parse saved auth data when getting user data');
        this.clearAuthData();
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // Check if token is too old (more than 23 hours - giving 1 hour buffer for refresh)
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        const tokenAge = Date.now() - (parsedAuth.timestamp || 0);
        const maxAge = 23 * 60 * 60 * 1000; // 23 hours in milliseconds
        
        if (tokenAge > maxAge) {
          console.log('Token too old, clearing auth data');
          this.clearAuthData();
          return false;
        }
      } catch (error) {
        console.error('Failed to check token age:', error);
        this.clearAuthData();
        return false;
      }
    }
    
    return true;
  }

  async logout(): Promise<void> {
    try {
      console.log('AuthService: Starting logout API call...');
      // Send logout request to clear the HttpOnly cookie
      await fetch(`${API_BASE_URL}/ModeratorAuth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': this.getToken() ? `Bearer ${this.getToken()}` : '',
          'X-Tenant-ID': TENANT_ID
        }
      });
      console.log('AuthService: Logout API call completed');
    } catch (error) {
      console.error('AuthService: Logout API call failed:', error);
    } finally {
      // Always clear auth data regardless of API call success
      console.log('AuthService: Clearing auth data...');
      this.clearAuthData();
      console.log('AuthService: Auth data cleared');
    }
  }

  private clearAuthData(): void {
    console.log('AuthService: Clearing user data and refresh promise...');
    this.userData = null;
    this.refreshPromise = null;
    console.log('AuthService: Removing localStorage items...');
    localStorage.removeItem('authData');
    localStorage.removeItem('isAuthenticated');
    console.log('AuthService: Notifying auth change listeners...');
    this.notifyAuthChange();
    console.log('AuthService: Auth data clear completed');
  }

  addAuthChangeListener(listener: () => void): void {
    this.authChangeListeners.push(listener);
  }

  removeAuthChangeListener(listener: () => void): void {
    this.authChangeListeners = this.authChangeListeners.filter(l => l !== listener);
  }

  private notifyAuthChange(): void {
    this.authChangeListeners.forEach(listener => listener());
  }

  async refreshToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check rate limit
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.minRefreshInterval) {
      console.log('Refresh rate limited');
      return false;
    }

    this.lastRefreshAttempt = now;
    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    
    // Clear the promise after completion
    this.refreshPromise = null;
    
    return result;
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      console.log('Attempting to refresh token...');
      
      const refreshResponse = await fetch(`${API_BASE_URL}/ModeratorAuth/refresh-token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Tenant-ID': TENANT_ID,
        },
      });
      
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        console.log('Refresh response:', refreshData);
        
        // Extract token from response structure
        if (refreshData?.success && refreshData?.data?.token) {
          const newToken = refreshData.data.token;
          const newUserData = refreshData.data.moderator;
          
          console.log('New token obtained, updating auth data...');
          this.setAuthData(newToken, newUserData || this.getUserData());
          
          return true;
        } else {
          console.error('No token found in refresh response:', refreshData);
          return false;
        }
      } else if (refreshResponse.status === 400) {
        console.error('Refresh token is invalid or expired');
        this.clearAuthData();
        window.location.href = '/login';
        return false;
      } else {
        console.error('Refresh token request failed:', refreshResponse.status, refreshResponse.statusText);
        return false;
      }
    } catch (error) {
      console.error('Error during token refresh:', error);
      return false;
    }
  }
}

export const authService = new AuthService();

export async function authFetch(input: RequestInfo, init: RequestInit = {}, retry = true): Promise<Response> {
  let token = authService.getToken();
  let headers = new Headers(init.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Add tenant ID header for multi-tenant support
  headers.set('X-Tenant-ID', TENANT_ID);
  
  // Always send cookies for refresh
  const fetchOptions: RequestInit = {
    ...init,
    headers,
    credentials: 'include',
  };
  
  let response = await fetch(input, fetchOptions);
  
  if (response.status === 401 && retry) {
    console.log('Token expired, attempting to refresh...');
    
    const refreshSuccess = await authService.refreshToken();
    
    if (refreshSuccess) {
      // Retry original request with new token
      const newToken = authService.getToken();
      headers.set('Authorization', `Bearer ${newToken}`);
      headers.set('X-Tenant-ID', TENANT_ID);
      const retryOptions: RequestInit = {
        ...init,
        headers,
        credentials: 'include',
      };
      
      return fetch(input, retryOptions);
    } else {
      // If refresh fails, logout and redirect
      console.log('Token refresh failed, logging out...');
      await authService.logout();
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
  }
  
  return response;
}

export const apiUrl = (path: string): string => {
  if (path.startsWith('/')) path = path.slice(1);
  return `${API_BASE_URL}/${path}`;
};

// Helper function to get full image URL
export const getImageUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) return null;
  
  // If the path already starts with http/https, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Construct the base URL for images by removing /api from the end
  const baseUrl = 'https://api.ibrahim-magdy.com';
  
  // Ensure the imagePath starts with /
  const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  return `${baseUrl}${path}`;
};

// Books API functions
export const booksApi = {
  // Get all books (Admin only)
  getBooks: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get active books
  getActiveBooks: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/active?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get books by grade (Admin only)
  getBooksByGrade: async (grade: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/grade/${grade}?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get active books by grade
  getActiveBooksbyGrade: async (grade: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/active/grade/${grade}?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get book by ID
  getBookById: async (id: string) => {
    const response = await authFetch(apiUrl(`Book/${id}`));
    return response.json();
  },

  // Create book without image
  createBook: async (bookData: any) => {
    const response = await authFetch(apiUrl('Book'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookData),
    });
    return response.json();
  },

  // Create book with image
  createBookWithImage: async (formData: FormData) => {
    const response = await authFetch(apiUrl('Book/with-image'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },

  // Update book without image
  updateBook: async (id: string, bookData: any) => {
    const response = await authFetch(apiUrl(`Book/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookData),
    });
    return response.json();
  },

  // Update book with image
  updateBookWithImage: async (id: string, formData: FormData) => {
    const response = await authFetch(apiUrl(`Book/${id}/with-image`), {
      method: 'PUT',
      body: formData,
    });
    return response.json();
  },

  // Delete book
  deleteBook: async (id: string) => {
    const response = await authFetch(apiUrl(`Book/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },

  // Upload book image
  uploadBookImage: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('imageFile', imageFile);
    
    const response = await authFetch(apiUrl('Book/upload-image'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },
};

// Unit Codes API functions
export const unitCodesApi = {
  // Generate unit codes
  generateCodeUnit: async (unitId: number, count: number) => {
    const response = await authFetch(apiUrl(`UnitCode/GenerateCodeUnit?unitId=${unitId}&count=${count}`), {
      method: 'POST',
    });
    return response.json();
  },

  // Generate general code
  generateGeneralCode: async (unitId: number, code: string) => {
    const response = await authFetch(apiUrl(`UnitCode/GenerateGeneralCode?unitId=${unitId}&generalCode=${encodeURIComponent(code)}`), {
      method: 'POST',
    });
    return response.json();
  },

  // Get all codes
  getAllCodes: async (codeType: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllCodes?codeType=${codeType}&pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all general codes
  getAllGeneralCodes: async (page: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllGeneralCodes?page=${page}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all codes count
  getAllCodesCount: async () => {
    const response = await authFetch(apiUrl('UnitCode/GetAllCodesCount'));
    return response.json();
  },

  // Get all student enter code
  getAllStudentEnterCode: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllStudentEnterCode?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all student enter code count
  getAllStudentEnterCodeCount: async () => {
    const response = await authFetch(apiUrl('UnitCode/GetAllStudentEnterCodeCount'));
    return response.text(); // Returns plain number
  },

  // Filter unit codes
  filterUnitCodes: async (filters: any) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.append(key, value as string);
      }
    });
    
    const response = await authFetch(apiUrl(`UnitCode/FilterUnitCodes?${params.toString()}`));
    return response.json();
  },

  // Delete code
  deleteCode: async (id: string) => {
    const response = await authFetch(apiUrl(`UnitCode/DeleteCode/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

// Course tree API for getting units nested within courses
export const courseTreeApi = {
  getCourseTree: async (courseId: string) => {
    const response = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
      method: 'GET',
      headers: {
        'accept': '*/*'
      }
    });
    return response.json();
  }
};

// Course API functions
export const courseApi = {
  // Add course with image upload
  addCourseWithImage: async (formData: FormData) => {
    const response = await authFetch(apiUrl('Course/addWithImage'), {
      method: 'POST',
      body: formData, // Don't set Content-Type header for FormData
    });
    return response.json();
  },
};